/**
 * Soft UI Dashboard React - ReportsBarChart
 */

import { useRef, useEffect } from "react";
import Chart from "chart.js/auto";
import PropTypes from "prop-types";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import BarReportsChartItem from "examples/Charts/BarCharts/ReportsBarChart/ReportsBarChartItem";
import configs from "examples/Charts/BarCharts/ReportsBarChart/configs";

function ReportsBarChart({
  title,
  description = "",
  chart,
  items = [],
  color = "info",
  height = "19rem",
  showItems = true,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (chartInstance.current) chartInstance.current.destroy();
    const ctx = canvasRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, { type: "bar", data, options });
    return () => chartInstance.current?.destroy();
  }, [data, options]);

  const renderItems =
    (items || []).map(({ icon, label, progress }) => (
      <Grid item xs={6} sm={3} key={label}>
        <BarReportsChartItem
          color={color}
          icon={{ color: icon.color, component: icon.component }}
          label={label}
          progress={{ content: progress.content, percentage: progress.percentage }}
        />
      </Grid>
    ));

  return (
    <Card sx={{ height: "100%" }}>
      <SoftBox padding="1rem">
        {/* Gráfica principal */}
        <SoftBox
          variant="gradient"
          bgColor={color}
          borderRadius="lg"
          py={2}
          pr={0.5}
          mb={3}
          height={height}                          // ← usa la altura recibida
        >
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
        </SoftBox>

        {/* Título / descripción */}
        <SoftBox px={1}>
          <SoftBox mb={2}>
            <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
              {title}
            </SoftTypography>
            {description ? (
              <SoftTypography component="div" variant="button" color="text" fontWeight="regular">
                {description}
              </SoftTypography>
            ) : null}
          </SoftBox>

          {/* Bloque de ítems SOLO si se pide y existen */}
          {showItems && (items?.length > 0) && (
            <SoftBox py={1} px={0.5}>
              <Grid container spacing={2}>{renderItems}</Grid>
            </SoftBox>
          )}
        </SoftBox>
      </SoftBox>
    </Card>
  );
}

ReportsBarChart.defaultProps = {
  items: [],
  color: "info",
  height: "19rem",
  description: "",
  showItems: true,
};

ReportsBarChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.shape({
    labels: PropTypes.array.isRequired,
    datasets: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  }).isRequired,
  items: PropTypes.arrayOf(PropTypes.object),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showItems: PropTypes.bool,
};

export default ReportsBarChart;
